import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Loader2, LogOut, UserRound, Building2, Factory, ShieldCheck, LayoutDashboard } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { ROLE_HOME, ROLE_LABEL, ROLE_LOGIN, clearAuth } from "../lib/authApi";

const LOGINS = [
  { role: "customer", label: "Customer Login", Icon: Building2 },
  { role: "partner", label: "Partner Login", Icon: Factory },
  { role: "admin", label: "VECTRON Admin Login", Icon: ShieldCheck },
];
const PORTAL_LABEL = { customer: "Customer Portal", partner: "Partner Portal", admin: "Admin Dashboard" };
const pill = "inline-flex items-center gap-2 h-10 px-3 sm:px-4 rounded-full border border-border text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors duration-200 whitespace-nowrap shrink-0";

export const useLogout = () => {
  const navigate = useNavigate();
  return (role) => {
    clearAuth();
    navigate(ROLE_LOGIN[role] || "/portal/login");
  };
};

export const SignInMenu = () => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger className={pill} data-testid="header-signin-btn" aria-label="Sign in">
      <UserRound className="h-4 w-4" strokeWidth={1.5} />
      <span className="hidden md:inline">Sign In</span>
      <ChevronDown className="h-3.5 w-3.5 opacity-70" strokeWidth={1.5} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={8} className="w-60 rounded-2xl p-1.5" data-testid="header-signin-menu">
      <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-muted-foreground">Sign in as</DropdownMenuLabel>
      {LOGINS.map(({ role, label, Icon }) => (
        <DropdownMenuItem key={role} asChild className="rounded-xl py-2.5 cursor-pointer">
          <Link to={ROLE_LOGIN[role]} data-testid={`signin-${role}-link`}><Icon className="h-4 w-4 mr-2 text-primary" strokeWidth={1.5} />{label}</Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const AccountMenu = ({ user }) => {
  const logout = useLogout();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className={pill} data-testid="header-account-btn" aria-label="Account menu">
        <UserRound className="h-4 w-4 text-primary" strokeWidth={1.5} />
        <span className="hidden 2xl:inline max-w-[9rem] truncate">{user.name || user.email}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70" strokeWidth={1.5} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64 rounded-2xl p-1.5" data-testid="header-account-menu">
        <DropdownMenuLabel className="space-y-0.5">
          <p className="text-sm font-medium truncate" data-testid="account-name">{user.name || user.email}</p>
          <p className="text-[11px] uppercase tracking-widest text-primary" data-testid="account-role">{ROLE_LABEL[user.role] || user.role}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="rounded-xl py-2.5 cursor-pointer">
          <Link to={ROLE_HOME[user.role] || "/"} data-testid="account-portal-link"><LayoutDashboard className="h-4 w-4 mr-2" strokeWidth={1.5} />{PORTAL_LABEL[user.role] || "My account"}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => logout(user.role)} className="rounded-xl py-2.5 cursor-pointer text-destructive focus:text-destructive" data-testid="account-logout-btn">
          <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const AuthControl = ({ session }) => {
  if (session.status === "loading") return <span className={pill} aria-busy="true" data-testid="header-auth-loading"><Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /></span>;
  return session.status === "authed" ? <AccountMenu user={session.user} /> : <SignInMenu />;
};

const mobileLink = "block px-4 py-3 rounded-lg text-sm font-medium text-primary hover:bg-primary/10";

export const MobileAuthLinks = ({ session, onNavigate }) => {
  const logout = useLogout();
  if (session.status === "loading") return null;
  if (session.status === "authed") {
    const { user } = session;
    return (
      <div className="border-t border-border pt-2 mt-2" data-testid="mobile-account">
        <p className="px-4 py-2 text-xs text-muted-foreground truncate">{user.name || user.email} · {ROLE_LABEL[user.role] || user.role}</p>
        <NavLink to={ROLE_HOME[user.role] || "/"} onClick={onNavigate} className={mobileLink} data-testid="mobile-account-portal-link">{PORTAL_LABEL[user.role] || "My account"}</NavLink>
        <button type="button" onClick={() => { onNavigate(); logout(user.role); }} className={`${mobileLink} w-full text-left text-destructive hover:bg-destructive/10`} data-testid="mobile-account-logout-btn">Logout</button>
      </div>
    );
  }
  return (
    <div className="border-t border-border pt-2 mt-2" data-testid="mobile-signin">
      <p className="px-4 py-2 text-[11px] uppercase tracking-widest text-muted-foreground">Sign in as</p>
      {LOGINS.map(({ role, label }) => (
        <NavLink key={role} to={ROLE_LOGIN[role]} onClick={onNavigate} className={mobileLink} data-testid={`mobile-signin-${role}-link`}>{label}</NavLink>
      ))}
    </div>
  );
};
